import { useEffect, useRef, useState } from 'react'

interface TrustpilotWidgetProps {
  /** Trustpilot template ID — defaults to the micro-review-count widget */
  templateId?: string
  /** Display style — 'badge' for compact, 'carousel' for full reviews */
  variant?: 'badge' | 'carousel'
}

/**
 * Renders a Trustpilot TrustBox widget. Only mounts when the Trustpilot
 * bootstrap script is loaded (gated by TRUSTPILOT_BUSINESS_UNIT_ID env var
 * in the marketing layout).
 */
export default function TrustpilotWidget({
  templateId = '5419b6a8b0d04a076446a9ad',
  variant = 'badge',
}: TrustpilotWidgetProps) {
  const ref = useRef<HTMLDivElement>(null)
  // Read client-side only: `document` doesn't exist in the SSR renderer.
  // The server renders nothing here (a third-party embed has no SEO value);
  // the widget appears after hydration.
  const [businessUnitId, setBusinessUnitId] = useState<string | null>(null)

  useEffect(() => {
    setBusinessUnitId(
      document.querySelector<HTMLMetaElement>('meta[name="trustpilot-business-unit-id"]')?.content ?? null
    )
  }, [])

  useEffect(() => {
    const tp = (window as unknown as Record<string, unknown>).Trustpilot as
      | { loadFromElement: (el: HTMLElement, rebind?: boolean) => void }
      | undefined

    if (tp && ref.current) {
      tp.loadFromElement(ref.current, true)
    }
  }, [businessUnitId])

  if (!businessUnitId) return null

  return (
    <div
      ref={ref}
      className="trustpilot-widget"
      data-locale="en-US"
      data-template-id={templateId}
      data-businessunit-id={businessUnitId}
      data-style-height={variant === 'badge' ? '24px' : '140px'}
      data-style-width="100%"
      data-theme="dark"
    >
      <a
        href="https://www.trustpilot.com/review/wavbee.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-white/48 hover:text-white/64"
      >
        Trustpilot
      </a>
    </div>
  )
}
